import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import PublicPageShell from '../components/PublicPageShell';
import { guideApi } from '../services/api';

const PRIMARY = '#0087c1';
const INK = '#172033';
const AMBER = '#e59a21';

const slugify = (value) => value
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

const parseGuide = (markdown) => {
  const lines = markdown.split(/\r?\n/);
  const sections = [];
  const preamble = [];
  let title = 'Struxor User Guide';
  let current = null;

  lines.forEach((line) => {
    if (line.startsWith('# ')) {
      title = line.slice(2).trim();
      return;
    }

    if (line.startsWith('## ')) {
      if (current) {
        current.markdown = current.lines.join('\n').trim();
        sections.push(current);
      }
      const sectionTitle = line.slice(3).trim();
      current = {
        id: slugify(sectionTitle),
        title: sectionTitle,
        lines: [],
      };
      return;
    }

    if (current) current.lines.push(line);
    else preamble.push(line);
  });

  if (current) {
    current.markdown = current.lines.join('\n').trim();
    sections.push(current);
  }

  return {
    title,
    intro: preamble.join(' ').replace(/\s+/g, ' ').trim(),
    sections,
  };
};

const NavContent = memo(({ sections, activeSection, query, setQuery, onSelect }) => (
  <Box>
    <Typography sx={{ mb: 1.5, color: INK, fontSize: 12, fontWeight: 850, letterSpacing: 0.6, textTransform: 'uppercase' }}>
      Guide contents
    </Typography>
    <TextField
      fullWidth
      size="small"
      placeholder="Search the guide"
      value={query}
      onChange={(event) => setQuery(event.target.value)}
      inputProps={{ 'aria-label': 'Search the user guide' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
      sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', borderRadius: 2 } }}
    />
    <List dense disablePadding>
      {sections.map((section) => (
        <ListItem key={section.id} disablePadding sx={{ mb: 0.25 }}>
          <ListItemButton
            selected={activeSection === section.id}
            onClick={() => onSelect(section.id)}
            sx={{
              py: 0.65,
              px: 1.2,
              borderRadius: 1.5,
              '&.Mui-selected': {
                bgcolor: alpha(PRIMARY, 0.1),
                color: PRIMARY,
                '&:hover': { bgcolor: alpha(PRIMARY, 0.14) },
              },
            }}
          >
            <ListItemText
              primary={section.title}
              primaryTypographyProps={{
                fontSize: 12,
                lineHeight: 1.35,
                fontWeight: activeSection === section.id ? 750 : 550,
              }}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  </Box>
));

export default function Guide() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [guide, setGuide] = useState({ title: 'Struxor User Guide', intro: '', sections: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const loadGuide = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await guideApi.get();
      const parsed = parseGuide(response.data.content || '');
      setGuide(parsed);
      setActiveSection(parsed.sections[0]?.id || '');
    } catch {
      setError('The user guide could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGuide();
  }, [loadGuide]);

  const visibleSections = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return guide.sections;
    return guide.sections.filter((section) => `${section.title}\n${section.markdown}`.toLowerCase().includes(normalizedQuery));
  }, [guide.sections, query]);

  useEffect(() => {
    if (query || !guide.sections.length) return undefined;
    const observers = guide.sections.map((section) => {
      const element = document.getElementById(section.id);
      if (!element) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(section.id);
        },
        { rootMargin: '-18% 0px -68% 0px' },
      );
      observer.observe(element);
      return observer;
    });
    return () => observers.forEach((observer) => observer?.disconnect());
  }, [guide.sections, query]);

  const scrollToSection = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
    setMobileNavOpen(false);
  }, []);

  const markdownComponents = useMemo(() => ({
    h3: ({ children }) => (
      <Typography component="h3" variant="h6" sx={{ mt: 3, mb: 1, color: 'text.primary', fontWeight: 750 }}>{children}</Typography>
    ),
    h4: ({ children }) => (
      <Typography component="h4" variant="subtitle1" sx={{ mt: 2.5, mb: 1, color: 'text.primary', fontWeight: 750 }}>{children}</Typography>
    ),
    p: ({ children }) => (
      <Typography component="p" variant="body1" color="text.secondary" sx={{ mb: 1.6, lineHeight: 1.82 }}>{children}</Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ mt: 0.5, mb: 2, pl: 3, color: 'text.secondary', '& li': { pl: 0.5, mb: 0.65, lineHeight: 1.72 } }}>{children}</Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ mt: 0.5, mb: 2, pl: 3, color: 'text.secondary', '& li': { pl: 0.5, mb: 0.75, lineHeight: 1.72 } }}>{children}</Box>
    ),
    a: ({ href = '', children }) => {
      if (href.startsWith('/')) {
        return <Link component={RouterLink} to={href} color="primary" underline="hover" fontWeight={650}>{children}</Link>;
      }
      if (href.startsWith('#')) {
        return <Link href={href} color="primary" underline="hover" fontWeight={650}>{children}</Link>;
      }
      return <Link href={href} target="_blank" rel="noreferrer" color="primary" underline="hover" fontWeight={650}>{children}</Link>;
    },
    blockquote: ({ children }) => (
      <Box component="blockquote" sx={{ m: '20px 0', p: 2, bgcolor: alpha(AMBER, theme.palette.mode === 'light' ? 0.09 : 0.16), borderLeft: `4px solid ${AMBER}`, borderRadius: '0 10px 10px 0', '& p:last-of-type': { mb: 0 } }}>{children}</Box>
    ),
    table: ({ children }) => (
      <Box sx={{ overflowX: 'auto', mb: 2.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 520, '& th': { bgcolor: theme.palette.mode === 'light' ? '#edf4f8' : alpha(PRIMARY, 0.14), color: 'text.primary', fontWeight: 750, textAlign: 'left' }, '& th, & td': { px: 1.5, py: 1.1, borderBottom: `1px solid ${theme.palette.divider}`, fontSize: 13, lineHeight: 1.5 }, '& tr:last-child td': { borderBottom: 0 }, '& td': { color: 'text.secondary' } }}>{children}</Box>
      </Box>
    ),
    code: ({ children }) => (
      <Box component="code" sx={{ px: 0.6, py: 0.2, bgcolor: alpha(PRIMARY, 0.09), color: 'text.primary', borderRadius: 0.7, fontSize: '0.9em' }}>{children}</Box>
    ),
    hr: () => <Divider sx={{ my: 3 }} />,
  }), [theme]);

  return (
    <PublicPageShell>
      <Helmet>
        <title>User Guide — How to Use Struxor Construction Software | Struxor</title>
        <meta name="description" content="The complete Struxor user guide for quotes, projects, activities, plans, digital forms, RAMS, purchase orders, timesheets, AI assistance, permissions, and mobile offline work." />
        <link rel="canonical" href="https://www.struxorapp.com/guide" />
        <meta property="og:title" content="Struxor User Guide" />
        <meta property="og:description" content="Step-by-step guidance for running construction work in Struxor on web and mobile." />
        <meta property="og:url" content="https://www.struxorapp.com/guide" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'TechArticle',
          headline: guide.title,
          description: guide.intro,
          url: 'https://www.struxorapp.com/guide',
          author: { '@type': 'Organization', name: 'Struxor' },
          publisher: { '@type': 'Organization', name: 'Struxor' },
        })}</script>
      </Helmet>

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(125deg, #006a9f 0%, #0087c1 48%, #16a2c7 100%)',
          '&:before': {
            content: '""',
            position: 'absolute',
            width: 420,
            height: 420,
            borderRadius: '50%',
            right: -120,
            top: -270,
            border: '70px solid rgba(255,255,255,.08)',
          },
          '&:after': {
            content: '""',
            position: 'absolute',
            width: 280,
            height: 280,
            borderRadius: '50%',
            left: -170,
            bottom: -210,
            border: '55px solid rgba(255,255,255,.06)',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Chip label="WEB · MOBILE · OFFLINE" sx={{ mb: 2, color: '#fff', bgcolor: 'rgba(255,255,255,.16)', fontWeight: 800, letterSpacing: 0.8 }} />
          <Typography variant="h2" component="h1" sx={{ fontSize: { xs: '2.15rem', md: '3.2rem' }, fontWeight: 850, letterSpacing: '-0.035em' }}>
            {guide.title}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1.5, mx: 'auto', maxWidth: 760, opacity: 0.92, fontWeight: 450, lineHeight: 1.55, fontSize: { xs: '1rem', md: '1.15rem' } }}>
            {guide.intro || 'Clear, practical guidance for every stage of work in Struxor.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} justifyContent="center" sx={{ mt: 3 }}>
            <Button component={RouterLink} to="/register" variant="contained" sx={{ bgcolor: '#fff', color: PRIMARY, fontWeight: 800, '&:hover': { bgcolor: '#eef8fc' } }}>
              Start free trial
            </Button>
            <Button component={RouterLink} to="/login" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,.7)', color: '#fff', fontWeight: 750, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,.08)' } }}>
              Log in
            </Button>
          </Stack>
        </Container>
      </Box>

      {isMobile && !loading && !error && (
        <IconButton
          aria-label="Open guide contents"
          onClick={() => setMobileNavOpen(true)}
          sx={{ position: 'fixed', zIndex: 1200, right: 18, bottom: 18, width: 54, height: 54, bgcolor: PRIMARY, color: '#fff', boxShadow: 5, '&:hover': { bgcolor: '#006a9f' } }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer anchor="right" open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} PaperProps={{ sx: { width: 'min(88vw, 330px)', p: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" fontWeight={800}>User guide</Typography>
          <IconButton aria-label="Close guide contents" onClick={() => setMobileNavOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <NavContent sections={visibleSections} activeSection={activeSection} query={query} setQuery={setQuery} onSelect={scrollToSection} />
      </Drawer>

      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#f5f8fb' : 'background.default', borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 7 } }}>
          {loading && (
            <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}>
              <Stack alignItems="center" spacing={1.5}>
                <CircularProgress size={34} />
                <Typography color="text.secondary">Loading the latest guide…</Typography>
              </Stack>
            </Box>
          )}

          {!loading && error && (
            <Container maxWidth="sm" sx={{ py: 6 }}>
              <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadGuide}>Try again</Button>}>{error}</Alert>
            </Container>
          )}

          {!loading && !error && (
            <Grid container spacing={4}>
              {!isMobile && (
                <Grid item md={3}>
                  <Paper elevation={0} sx={{ position: 'sticky', top: 76, maxHeight: 'calc(100vh - 96px)', overflowY: 'auto', p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                    <NavContent sections={visibleSections} activeSection={activeSection} query={query} setQuery={setQuery} onSelect={scrollToSection} />
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} md={9}>
                {visibleSections.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 4, border: `1px solid ${theme.palette.divider}`, borderRadius: 3, textAlign: 'center' }}>
                    <SearchIcon sx={{ fontSize: 42, color: 'text.disabled' }} />
                    <Typography variant="h6" sx={{ mt: 1, fontWeight: 750 }}>No guide sections match “{query}”</Typography>
                    <Button sx={{ mt: 1 }} onClick={() => setQuery('')}>Clear search</Button>
                  </Paper>
                ) : visibleSections.map((section) => (
                  <Paper
                    component="section"
                    id={section.id}
                    key={section.id}
                    elevation={0}
                    sx={{
                      mb: 3,
                      p: { xs: 2.2, sm: 3.5, md: 4 },
                      scrollMarginTop: 82,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5 }}>
                      <Box sx={{ width: 8, height: 34, borderRadius: 4, bgcolor: PRIMARY, flexShrink: 0 }} />
                      <Typography component="h2" variant="h4" sx={{ fontSize: { xs: '1.45rem', sm: '1.8rem' }, fontWeight: 820, letterSpacing: '-0.025em' }}>
                        {section.title}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {section.markdown}
                    </ReactMarkdown>
                  </Paper>
                ))}
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </PublicPageShell>
  );
}
